import React, { useState, useEffect } from 'react';
import { ChevronRight, MapPin, AlertTriangle, Clock, Phone, MessageCircle, Zap, Target, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

const InteractiveDiagnostic = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    pest: '',
    urgency: '',
    location: ''
  });
  const [showResult, setShowResult] = useState(false);
  const [dronePosition, setDronePosition] = useState({ x: 50, y: 50 });

  // Drone animation
  useEffect(() => {
    const interval = setInterval(() => {
      setDronePosition({
        x: 30 + Math.random() * 40,
        y: 40 + Math.random() * 20
      });
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const pestTypes = [
    { 
      id: 'frelon-asiatique', 
      label: '🐝 Frelon Asiatique', 
      danger: 95,
      description: 'Espèce invasive très dangereuse',
      color: 'bg-red-500'
    },
    { 
      id: 'frelon-europeen', 
      label: '🐛 Frelon Européen', 
      danger: 75,
      description: 'Agressif près du nid',
      color: 'bg-orange-500'
    },
    { 
      id: 'guepe', 
      label: '🐞 Guêpes', 
      danger: 60,
      description: 'Peut attaquer en essaim',
      color: 'bg-yellow-500'
    },
    { 
      id: 'autre', 
      label: '🦟 Autre nuisible', 
      danger: 40,
      description: 'À identifier rapidement',
      color: 'bg-blue-500'
    }
  ];

  const urgencyLevels = [
    { 
      id: 'tranquille', 
      label: '😌 Tranquille', 
      description: 'Pas de danger immédiat',
      multiplier: 1,
      icon: '🟢'
    },
    { 
      id: 'urgent', 
      label: '⚠️ Urgent', 
      description: 'Nid visible, activité importante',
      multiplier: 1.3,
      icon: '🟡'
    },
    { 
      id: 'critique', 
      label: '🚨 Critique', 
      description: 'Danger immédiat, près des habitations',
      multiplier: 1.6,
      icon: '🔴'
    }
  ];

  const handleAnswer = (key, value) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
    if (step < 2) {
      setTimeout(() => setStep(step + 1), 300);
    } else {
      setTimeout(() => setShowResult(true), 500);
    }
  };

  const getDangerScore = () => {
    const pest = pestTypes.find(p => p.id === answers.pest);
    const urgency = urgencyLevels.find(u => u.id === answers.urgency);
    if (!pest || !urgency) return 0;
    return Math.min(100, Math.round(pest.danger * urgency.multiplier));
  };

  const getRecommendation = () => {
    const score = getDangerScore();
    if (score >= 90) return {
      level: 'CRITIQUE',
      color: 'text-red-600 bg-red-50 border-red-200',
      action: 'Intervention immédiate recommandée',
      time: 'sous 2h'
    };
    if (score >= 70) return {
      level: 'ÉLEVÉ', 
      color: 'text-orange-600 bg-orange-50 border-orange-200',
      action: 'Intervention rapide conseillée',
      time: 'sous 24h'
    };
    return {
      level: 'MODÉRÉ',
      color: 'text-yellow-600 bg-yellow-50 border-yellow-200', 
      action: 'Planifier une intervention',
      time: 'sous 48h'
    };
  };

  const handleCall = () => {
    window.location.href = 'tel:0676583038';
  };

  const handleWhatsApp = () => {
    const message = `Diagnostic Frelon33: ${answers.pest} - Urgence: ${answers.urgency} - Lieu: ${answers.location} - Score danger: ${getDangerScore()}%`;
    window.open(`https://wa.me/33676583038?text=${encodeURIComponent(message)}`, '_blank');
  };

  const reset = () => {
    setStep(0);
    setAnswers({ pest: '', urgency: '', location: '' });
    setShowResult(false);
  };

  if (showResult) {
    const recommendation = getRecommendation();
    const dangerScore = getDangerScore();
    
    return (
      <div className="relative bg-gradient-to-br from-gray-900 via-black to-gray-900 rounded-2xl p-8 text-white overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-4 left-4 w-2 h-2 bg-yellow-400 rounded-full animate-ping"></div>
          <div className="absolute top-12 right-8 w-1 h-1 bg-red-400 rounded-full animate-pulse"></div>
          <div className="absolute bottom-8 left-12 w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce"></div>
        </div>

        <div className="relative z-10 space-y-6">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Target className="h-6 w-6 text-yellow-400" />
              <h3 className="text-2xl font-bold">Diagnostic Terminé</h3>
            </div>
            
            {/* Danger Score Gauge */}
            <div className="relative w-32 h-32 mx-auto mb-6">
              <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="50" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8"/>
                <circle 
                  cx="60" cy="60" r="50" fill="none" 
                  stroke={dangerScore >= 90 ? "#dc2626" : dangerScore >= 70 ? "#ea580c" : "#eab308"}
                  strokeWidth="8"
                  strokeDasharray={`${dangerScore * 3.14} 314`}
                  className="transition-all duration-1000 ease-out"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-3xl font-bold">{dangerScore}%</div>
                  <div className="text-xs opacity-75">DANGER</div>
                </div>
              </div>
            </div>

            <div className={`inline-block px-4 py-2 rounded-full border font-semibold ${recommendation.color}`}>
              NIVEAU {recommendation.level}
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 space-y-4">
            <div className="flex items-center space-x-3">
              <Zap className="h-5 w-5 text-yellow-400" />
              <span className="font-semibold">{recommendation.action}</span>
            </div>
            <div className="flex items-center space-x-3">
              <Clock className="h-5 w-5 text-blue-400" />
              <span>Intervention recommandée {recommendation.time}</span>
            </div>
            <div className="flex items-center space-x-3">
              <MapPin className="h-5 w-5 text-green-400" />
              <span>{answers.location || 'Localisation à préciser'}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Button
              onClick={handleCall}
              className="bg-red-600 hover:bg-red-700 text-white font-bold py-4 flex items-center justify-center space-x-2 transform hover:scale-105 transition-all duration-200"
            >
              <Phone className="h-5 w-5" />
              <span>Appeler</span>
            </Button>
            <Button
              onClick={handleWhatsApp}
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-4 flex items-center justify-center space-x-2 transform hover:scale-105 transition-all duration-200"
            >
              <MessageCircle className="h-5 w-5" />
              <span>WhatsApp</span>
            </Button>
            <Button
              onClick={reset}
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-black font-bold py-4 transition-all duration-200"
            >
              Recommencer
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card className="relative bg-gradient-to-br from-yellow-50 to-white border-2 border-yellow-200 overflow-hidden">
      {/* Drone Animation */}
      <div 
        className="absolute w-8 h-8 text-yellow-600 transition-all duration-3000 ease-in-out opacity-30"
        style={{ 
          left: `${dronePosition.x}%`, 
          top: `${dronePosition.y}%`,
          transform: 'translate(-50%, -50%)'
        }}
      >
        🚁
      </div>

      <CardContent className="p-8">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Target className="h-6 w-6 text-yellow-600" />
            <h3 className="text-2xl font-bold text-gray-900">Diagnostic Express</h3>
          </div>
          <p className="text-gray-600">Évaluez votre situation en 30 secondes</p>
          
          {/* Progress Bar */}
          <div className="mt-6 bg-gray-200 rounded-full h-2 overflow-hidden">
            <div 
              className="bg-gradient-to-r from-yellow-400 to-yellow-600 h-2 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${((step + 1) / 3) * 100}%` }}
            ></div>
          </div>
          <p className="text-sm text-gray-500 mt-2">Étape {step + 1} sur 3</p>
        </div>

        {step === 0 && (
          <div className="space-y-4">
            <h4 className="text-xl font-semibold text-center mb-6">🔍 Quel type de nuisible ?</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {pestTypes.map((pest) => (
                <button
                  key={pest.id}
                  onClick={() => handleAnswer('pest', pest.id)}
                  className="group p-4 border-2 border-gray-200 rounded-xl hover:border-yellow-400 hover:bg-yellow-50 transition-all duration-200 transform hover:scale-105"
                >
                  <div className="text-center space-y-2">
                    <div className="text-2xl">{pest.label.split(' ')[0]}</div>
                    <div className="font-semibold">{pest.label.substring(2)}</div>
                    <div className="text-sm text-gray-500">{pest.description}</div>
                    <div className="flex items-center justify-center space-x-2">
                      <div className={`w-3 h-3 rounded-full ${pest.color}`}></div>
                      <span className="text-xs font-medium">Danger: {pest.danger}%</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <h4 className="text-xl font-semibold text-center mb-6">⚡ Niveau d'urgence ?</h4>
            <div className="space-y-4">
              {urgencyLevels.map((level) => (
                <button
                  key={level.id}
                  onClick={() => handleAnswer('urgency', level.id)}
                  className="w-full p-4 border-2 border-gray-200 rounded-xl hover:border-yellow-400 hover:bg-yellow-50 transition-all duration-200 transform hover:scale-102 text-left group"
                >
                  <div className="flex items-center space-x-4">
                    <div className="text-3xl">{level.icon}</div>
                    <div className="flex-1">
                      <div className="font-semibold text-lg">{level.label}</div>
                      <div className="text-gray-500">{level.description}</div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-yellow-600 transition-colors" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <h4 className="text-xl font-semibold text-center mb-6">📍 Où se trouve le nid ?</h4>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Ville ou adresse exacte (ex: Bordeaux)"
                className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-yellow-400 focus:outline-none transition-colors"
                onChange={(e) => setAnswers(prev => ({ ...prev, location: e.target.value }))}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && answers.location.trim()) {
                    handleAnswer('location', answers.location);
                  }
                }}
              />
              <Button
                onClick={() => handleAnswer('location', answers.location)}
                disabled={!answers.location.trim()}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200"
              >
                <Target className="h-5 w-5 mr-2" />
                Analyser la situation
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InteractiveDiagnostic;